import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  DocumentConfiguration,
  IDocumentRequirementItem,
} from '../../../../utils/types/JobOpenings';
import type { IDocumentChecklistItem } from '../interfaces';

export interface IUseDocumentChecklist {
  activeItems: IDocumentChecklistItem[];
  attachedCount: number;
  allDone: boolean;
  hasPendingMandatory: boolean;
  firstPendingMandatoryIndex: number;
  focusedType: string;
  setFocusedType: (key: string) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  getIsLocked: (itemKey: string, index: number) => boolean;
  processFiles: (files: FileList | null) => void;
  handleFileChange: (files: FileList | null) => void;
  handleDrop: (files: FileList) => void;
}

interface IUseDocumentChecklistParams {
  configurations?: DocumentConfiguration[];
  documentRequirements?: IDocumentRequirementItem[];
  selectedFiles: Record<string, File[]>;
  onFileSelect: (type: string, file: File | null) => void;
  onBatchFileSelect?: (updates: Record<string, File | null>) => void;
  disabled?: boolean;
}

const toChecklistItem = (
  type: string,
  key: string,
  requirement: string,
  description: string,
  allowMultiple: boolean,
): IDocumentChecklistItem => ({ type, key, requirement, description, allowMultiple });

export const useDocumentChecklist = ({
  configurations,
  documentRequirements,
  selectedFiles,
  onFileSelect,
  onBatchFileSelect,
  disabled = false,
}: IUseDocumentChecklistParams): IUseDocumentChecklist => {
  const rawItems = useMemo<IDocumentChecklistItem[]>(
    () =>
      documentRequirements
        ? documentRequirements.map((req) =>
          toChecklistItem(
            req.label,
            req.key || req.label,
            req.isMandatory ? 'Mandatory' : 'Optional',
            req.description || `Please upload your ${req.label}`,
            req.allowMultiple ?? false,
          ),
        )
        : configurations
          ?.filter((config) => config.requirement !== 'Disabled')
          .map((config) =>
            toChecklistItem(
              config.type,
              config.type,
              config.requirement,
              config.description ?? `Please upload your ${config.type}`,
              config.allowMultiple ?? false,
            ),
          ) || [],
    [documentRequirements, configurations],
  );

  const activeItems = useMemo<IDocumentChecklistItem[]>(
    () =>
      [...rawItems].sort((a, b) => {
        if (a.requirement === 'Mandatory' && b.requirement !== 'Mandatory') return -1;
        if (a.requirement !== 'Mandatory' && b.requirement === 'Mandatory') return 1;
        return 0;
      }),
    [rawItems],
  );

  const attachedCount = useMemo(() => Object.keys(selectedFiles).length, [selectedFiles]);

  const firstPendingMandatoryIndex = useMemo(
    () =>
      activeItems.findIndex(
        (item) => item.requirement === 'Mandatory' && !(selectedFiles[item.key]?.length > 0),
      ),
    [activeItems, selectedFiles],
  );

  const hasPendingMandatory = firstPendingMandatoryIndex !== -1;

  const [focusedType, setFocusedType] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (hasPendingMandatory) {
      setFocusedType(activeItems[firstPendingMandatoryIndex].key);
    }
  }, [hasPendingMandatory, firstPendingMandatoryIndex, activeItems]);

  const getIsLocked = (itemKey: string, index: number): boolean => {
    const item = activeItems[index];
    if (!item) return false;
    if (item.requirement === 'Mandatory') {
      return hasPendingMandatory && index > firstPendingMandatoryIndex;
    }
    return hasPendingMandatory;
  };

  useEffect(() => {
    if (focusedType && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [focusedType]);

  const processFiles = (files: FileList | null): void => {
    if (!files || files.length === 0 || disabled) return;

    if (focusedType) {
      const item = activeItems.find((entry) => entry.key === focusedType);
      if (item?.allowMultiple) {
        Array.from(files).forEach((file) => onFileSelect(focusedType, file));
      } else {
        onFileSelect(focusedType, files[0]);
      }
    } else if (onBatchFileSelect) {
      const availableItems = activeItems.filter(
        (item, idx) =>
          (item.allowMultiple || !(selectedFiles[item.key]?.length > 0)) && !getIsLocked(item.key, idx),
      );
      if (availableItems.length > 0) {
        const updates: Record<string, File | null> = {};
        Array.from(files).forEach((file, index) => {
          if (index < availableItems.length) {
            updates[availableItems[index].key] = file;
          }
        });
        if (Object.keys(updates).length > 0) {
          onBatchFileSelect(updates);
        }
      }
    }
    setFocusedType('');
  };

  const handleFileChange = (files: FileList | null): void => {
    processFiles(files);
  };

  const handleDrop = (files: FileList): void => {
    processFiles(files);
  };

  const allDone = activeItems.length > 0 && activeItems.every((item) => selectedFiles[item.key]?.length > 0);

  return {
    activeItems,
    attachedCount,
    allDone,
    hasPendingMandatory,
    firstPendingMandatoryIndex,
    focusedType,
    setFocusedType,
    fileInputRef,
    getIsLocked,
    processFiles,
    handleFileChange,
    handleDrop,
  };
};
